import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { validateEmail } from "../auth/validationHelpers";
import { ErrorBox, OrgModal, PrimaryButton } from "./OrgModal";
import RoleOptions from "./RoleOptions";
import { approveJoinRequest, can, inviteMember, orgErrorMessage, readSession, resendInvite } from "../../utils/organisationApi";

// Invite by email and role. Names are optional. Any address is allowed, including Gmail.
const InviteMemberModal = ({ isOpen, onClose, onInvited, roles, organisationName }) => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmail("");
    setFirstName("");
    setLastName("");
    setRole(roles[0] || "");
    setError("");
    setConflict(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const emailError = validateEmail(email.trim());
    if (emailError) {
      setError(emailError);
      return;
    }
    if (!role) {
      setError("Choose a role.");
      return;
    }
    setSaving(true);
    setError("");
    setConflict(null);
    try {
      await inviteMember({
        email: email.trim(),
        role,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      onInvited(email.trim());
      onClose();
    } catch (err) {
      const code = err?.response?.data?.code;
      const inviteId = err?.response?.data?.details?.invite_id;
      if ((code === "INVITE_EXISTS" || code === "JOIN_REQUEST_EXISTS") && inviteId) {
        setConflict({ code, inviteId });
      }
      setError(orgErrorMessage(err, "Could not send the invite."));
    } finally {
      setSaving(false);
    }
  };

  const handleConflict = async () => {
    if (!conflict) return;
    setSaving(true);
    setError("");
    try {
      if (conflict.code === "INVITE_EXISTS") {
        await resendInvite(conflict.inviteId);
      } else {
        await approveJoinRequest(conflict.inviteId, role);
      }
      onInvited(email.trim());
      onClose();
    } catch (err) {
      setError(orgErrorMessage(err, "Could not update the invite."));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { borderColor: theme.palette.secondary.v4 };
  const canResolveConflict =
    conflict?.code === "INVITE_EXISTS" ||
    (conflict?.code === "JOIN_REQUEST_EXISTS" && can(readSession(), "approveJoinRequests"));
  const conflictLabel = conflict?.code === "JOIN_REQUEST_EXISTS" ? "Approve request" : "Resend invite";

  return (
    <OrgModal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      titleId="invite-member-title"
      title="Invite Member"
      footer={
        canResolveConflict ? (
          <PrimaryButton onClick={handleConflict} disabled={saving}>
            {saving ? "Working..." : conflictLabel}
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Sending..." : "Send Invite"}
          </PrimaryButton>
        )
      }
    >
      <p className="text-sm text-accent-gray">
        They'll get an email to join {organisationName ? <span className="font-semibold">{organisationName}</span> : "your organisation"}.
        The link lasts 7 days, and their access starts when they accept.
      </p>
      <form onSubmit={canResolveConflict ? handleConflict : handleSubmit} className="flex flex-col gap-5">
        <div>
          <label htmlFor="invite-email" className="block text-sm font-medium mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setConflict(null);
            }}
            placeholder="name@company.com"
            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
            autoFocus
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label htmlFor="invite-first-name" className="block text-sm font-medium mb-2">
              First name
            </label>
            <input
              id="invite-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={inputStyle}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="invite-last-name" className="block text-sm font-medium mb-2">
              Last name
            </label>
            <input
              id="invite-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={inputStyle}
            />
          </div>
        </div>
        <RoleOptions
          name="invite-role"
          roles={roles}
          value={role}
          onChange={(nextRole) => {
            setRole(nextRole);
            setConflict(null);
          }}
        />
      </form>
      <ErrorBox>{error}</ErrorBox>
    </OrgModal>
  );
};

export default InviteMemberModal;
